import { useEffect } from "react";
import type { ReactNode } from "react";
import { Outlet, useLocation, Navigate } from "react-router-dom";
import { AppSidebar } from "~/components/shell/AppSidebar";
import { AppTopbar } from "~/components/shell/AppTopbar";
import { AppMobileNav } from "~/components/shell/AppMobileNav";
import { AppMobileTopbar } from "~/components/shell/AppMobileTopbar";
import { AppMobileDrawer } from "~/components/shell/AppMobileDrawer";
import { AppToast } from "~/components/shell/AppToast";
import { useIsAuthed } from "~/stores/authStore";
import { useProductsStore } from "~/stores/productsStore";
import { useAccountStore } from "~/stores/accountStore";
import { useT } from "~/hooks/useT";

// Route-prefix → breadcrumb token keys. Matched by `/segment` so nested
// routes (e.g. `/orders/FND-10482`) still resolve to the parent crumb.
const CRUMB_BY_PATH: Record<string, string[]> = {
  "/dashboard": ["dashboard"],
  "/catalog": ["dashboard", "catalog"],
  "/cart": ["dashboard", "your_cart"],
  "/orders": ["dashboard", "orders"],
  "/invoices": ["dashboard", "invoices"],
  "/account": ["dashboard", "account"],
};

export function RequireAuth({ children }: { children: ReactNode }) {
  const isAuthed = useIsAuthed();
  if (!isAuthed) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function AppLayout() {
  const { t } = useT();
  const { pathname } = useLocation();
  // Take the first path segment, with a leading slash, so we can look it up
  // in CRUMB_BY_PATH without writing every nested route explicitly.
  const base = `/${pathname.split("/").filter(Boolean).slice(0, 1).join("/")}`;
  const crumbKeys = CRUMB_BY_PATH[base] ?? [];
  const crumbs = crumbKeys.map((k) => t(k));
  const loadProducts = useProductsStore((s) => s.load);
  const loadAccount = useAccountStore((s) => s.load);
  useEffect(() => {
    void loadProducts();
    void loadAccount();
  }, [loadProducts, loadAccount]);
  return (
    <div className="app">
      <AppSidebar />
      <AppMobileTopbar />
      <AppMobileDrawer />
      <main className="main">
        <AppTopbar crumbs={crumbs} />
        <Outlet />
      </main>
      <AppMobileNav />
      <AppToast />
    </div>
  );
}
